/**
 * The company record as a page (prototype board 4): "Open as page" from the
 * company panel. It IS the record -- lists point at it -- so it carries no list
 * actions; the tabs are the panel's (Overview, People we know here, Sources,
 * Lists & searches), and `?tab=` keeps the open one shareable.
 */
import { Spinner } from "@nu-assist/ui";
import { ArrowLeft } from "lucide-react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { Link } from "react-router-dom";

import {
  type CompanyListsClient,
  createCompanyListsClient,
} from "../../clients/companyLists.client";
import {
  CompanyHeader,
  CompanyListsOn,
  CompanyOverview,
  CompanySources,
  type CompanyTab,
  CompanyTabs,
  PeopleWeKnow,
  sourceCount,
} from "../../features/company-lists/CompanyRecordParts";
import { COMPANY_LISTS_PATH } from "../../features/company-lists/paths";
import {
  useAvatarUrls,
  useCompanyPeople,
  useCompanyRecord,
  useLogoUrls,
} from "../../features/company-lists/queries";

const API_BASE = import.meta.env.VITE_API_URL ?? "https://api.local.nu-assist.com";
const defaultClient = createCompanyListsClient({ apiBase: API_BASE });

const TABS: readonly CompanyTab[] = ["company", "people", "sources", "lists"];

function backTarget(state: unknown): string {
  const from = (state as { from?: unknown } | null)?.from;
  return typeof from === "string" && from.startsWith("/") ? from : COMPANY_LISTS_PATH;
}

export function CompanyRecordPage({ client = defaultClient }: { client?: CompanyListsClient }) {
  const { companyId = "" } = useParams<{ companyId: string }>();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const raw = searchParams.get("tab");
  const tab: CompanyTab = TABS.includes(raw as CompanyTab) ? (raw as CompanyTab) : "company";
  const recordQuery = useCompanyRecord(client, companyId);
  const peopleQuery = useCompanyPeople(client, companyId);
  const record = recordQuery.data;
  const people = peopleQuery.data;
  const logoUrls = useLogoUrls(client, [record]);
  const avatarUrls = useAvatarUrls(client, people?.items ?? []);

  function select(next: CompanyTab) {
    setSearchParams(
      (prev) => {
        const p = new URLSearchParams(prev);
        if (next === "company") p.delete("tab");
        else p.set("tab", next);
        return p;
      },
      { replace: true, state: location.state }
    );
  }

  if (recordQuery.isPending) {
    return (
      <div className="flex justify-center p-12">
        <Spinner label="Loading the company…" />
      </div>
    );
  }
  if (!record) {
    return (
      <div className="flex flex-col gap-4 p-8" data-testid="company-page-missing">
        <p className="text-body text-text-secondary">This company doesn’t exist.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 p-8" data-testid="company-page">
      <Link
        to={backTarget(location.state)}
        className="inline-flex w-fit items-center gap-1 text-label text-text-secondary hover:text-text"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        Company lists
      </Link>

      <div className="rounded-lg border border-border bg-surface p-5">
        <CompanyHeader
          record={record}
          as="h1"
          logoUrl={record.logo_s3_key ? logoUrls.get(record.logo_s3_key) : null}
        />
      </div>

      <div className="flex flex-col gap-5 lg:flex-row">
        <div className="flex min-w-0 flex-1 flex-col gap-4">
          <CompanyTabs
            active={tab}
            onSelect={select}
            labels={{
              company: "Overview",
              people: "People we know here",
              lists: "Lists & searches",
            }}
            counts={{
              people: record.people.current + record.people.alumni,
              sources: sourceCount(record, people),
              lists: record.lists.length,
            }}
          />
          {tab === "company" ? (
            <CompanyOverview
              record={record}
              people={people}
              avatarUrls={avatarUrls}
              onSeePeople={() => select("people")}
            />
          ) : tab === "people" ? (
            <PeopleWeKnow people={people} avatarUrls={avatarUrls} />
          ) : tab === "sources" ? (
            <CompanySources record={record} people={people} />
          ) : (
            <CompanyListsOn record={record} />
          )}
        </div>
        {tab !== "sources" && (
          <aside className="flex w-full flex-col gap-3 lg:w-80">
            <h2 className="text-h3 text-text">One record, many lists</h2>
            <CompanySources record={record} people={people} />
            <p className="text-caption text-text-muted">
              This page is the company record. Lists point at it, so a correction here reaches every
              list.
            </p>
          </aside>
        )}
      </div>
    </div>
  );
}
